import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import {
  faArrowRightLong,
  faArrowRotateRight,
  faBolt,
  faClock,
  faGaugeHigh,
  faHeartbeat,
  faTemperatureThreeQuarters,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Paper, LoadingOverlay, DefaultMantineColor } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { ElevationIcon } from "assets/icons/ElevationIcon";
// Types only - chart.js itself is loaded lazily with TrackChart
import type { Chart } from "chart.js";
import type { ChartDataPoint } from "containers/UI/ChartPanel/Chart/TrackChart";
import { BottomToggle } from "components/UI/Toggles/BottomToggle";
import { PANEL_TOGGLE_ANIMATION_DURATION } from "config/constants";
import { useTrackStats } from "stores/useTrackStats";
import useMeasure from "react-use-measure";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import {
  ChartXAxisType,
  ChartYAxisType,
  formatXAxisValue,
  getChartPoints,
  getDataset,
  getPopulatedYAxes,
  getXAxisTooltipFormatter,
  getYAxisTooltipFormatter,
  isChartYAxisType,
  Y_AXES,
} from "./chartData";
import { ChartHoverSync } from "containers/UI/ChartPanel/Chart/ChartHoverSync";
import { ChartMarkerClearButton } from "./Components/ChartMarkerClearButton";
import { ChartPanelMenu } from "./Components/ChartPanelMenu";

// chart.js is only needed once a track is shown, so it's kept out of the main bundle
const TrackChart = lazy(() =>
  import("containers/UI/ChartPanel/Chart/TrackChart").then((module) => ({
    default: module.TrackChart,
  }))
);

// height of the chart panel in % of viewport height
const CHART_PANEL_HEIGHT = 20;

export const ChartPanel = () => {
  const { state } = useRouting();
  const [xAxisType, setXAxisType] = useState<string | null>("distance");
  const [yAxisType, setYAxisType] = useState<string | null>("elevation");
  const {
    state: { units },
  } = useOptions();
  const [isOpen, setIsOpen] = useState(true);
  const [ref, { height, width }] = useMeasure();
  const { stats } = useTrackStats();
  const chartRef = useRef<Chart<"line", ChartDataPoint[], unknown> | null>(
    null
  );
  const [debounced] = useDebouncedValue(width, 80);
  const colorScheme = useShownColorScheme();

  useEffect(() => {
    chartRef.current?.resize();
  }, [debounced, stats?.chartData]);

  // The time axis needs the times of the track's points
  const validatedType: ChartXAxisType =
    stats?.totalTime && xAxisType === "time" ? "time" : "distance";
  const shownYAxisType: ChartYAxisType = isChartYAxisType(yAxisType)
    ? yAxisType
    : "elevation";

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const mainChartData = stats?.chartData
    ? getChartPoints(stats.chartData, "elevation", validatedType)
    : undefined;
  const currentDataset = stats?.chartData
    ? getDataset(stats.chartData, shownYAxisType, validatedType)
    : undefined;

  /** Formats labels on the bottom of the chart (horizontal axis) */
  const xAxisLabelFormatter = useCallback(
    (value: number | string) =>
      formatXAxisValue(validatedType, Number(value), units, 0),
    [units, validatedType]
  );

  /** Formats labels on the left side of the chart (vertical axis) */
  const yAxisLabelFormatter = useCallback(
    (value: number | string) =>
      Y_AXES[shownYAxisType].format(Number(value), units, 0),
    [units, shownYAxisType]
  );

  const yAxisTooltipFormatter = useMemo(
    () => getYAxisTooltipFormatter(shownYAxisType, validatedType, units),
    [shownYAxisType, units, validatedType]
  );

  const xAxisTooltipFormatter = useMemo(
    () => getXAxisTooltipFormatter(validatedType, units),
    [units, validatedType]
  );

  const hoverFormatters = useMemo(
    () => ({ xAxisTooltipFormatter, yAxisTooltipFormatter }),
    [xAxisTooltipFormatter, yAxisTooltipFormatter]
  );

  /** Change icons for individual xAxis types */
  const getTooltipXAxisIcon = useCallback(() => {
    if (validatedType === "distance")
      return <FontAwesomeIcon icon={faArrowRightLong} />;
    return <FontAwesomeIcon icon={faClock} />;
  }, [validatedType]);

  /** Change icons for individual yAxis types */
  const getTooltipYAxisIcon = useCallback(
    (color?: DefaultMantineColor) => {
      const getColorSchemeByTheme = () =>
        colorScheme === "light" ? "#0b0b0b" : "#b7b8bb";
      if (yAxisType === "speed") {
        return color ? (
          <FontAwesomeIcon icon={faGaugeHigh} />
        ) : (
          <FontAwesomeIcon color={getColorSchemeByTheme()} icon={faGaugeHigh} />
        );
      }
      if (yAxisType === "cadence") {
        return color ? (
          <FontAwesomeIcon icon={faArrowRotateRight} />
        ) : (
          <FontAwesomeIcon
            color={getColorSchemeByTheme()}
            icon={faArrowRotateRight}
          />
        );
      }
      if (yAxisType === "heartRate") {
        return color ? (
          <FontAwesomeIcon icon={faHeartbeat} />
        ) : (
          <FontAwesomeIcon color={getColorSchemeByTheme()} icon={faHeartbeat} />
        );
      }
      if (yAxisType === "power") {
        return color ? (
          <FontAwesomeIcon icon={faBolt} />
        ) : (
          <FontAwesomeIcon color={getColorSchemeByTheme()} icon={faBolt} />
        );
      }
      if (yAxisType === "temperature") {
        return color ? (
          <FontAwesomeIcon icon={faTemperatureThreeQuarters} />
        ) : (
          <FontAwesomeIcon
            color={getColorSchemeByTheme()}
            icon={faTemperatureThreeQuarters}
          />
        );
      }
      return color ? (
        <Box>
          <ElevationIcon />
        </Box>
      ) : (
        <Box style={{ color: getColorSchemeByTheme() }}>
          <ElevationIcon />
        </Box>
      );
    },
    [yAxisType, colorScheme]
  );

  const getYAxis = useCallback(
    (): ChartYAxisType[] =>
      stats?.chartData ? getPopulatedYAxes(stats.populatedDatasets) : [],
    [stats?.chartData, stats?.populatedDatasets]
  );

  const { routing, isRouting, areElevationsLoading } = state;

  // Another track may not have the chosen data (e.g. no heart rate)
  useEffect(() => {
    if (!isChartYAxisType(yAxisType) || !getYAxis().includes(yAxisType)) {
      setYAxisType("elevation");
    }
  }, [yAxisType, getYAxis]);

  return useMemo(
    () =>
      currentDataset || isRouting || areElevationsLoading ? (
        <Box
          ref={ref}
          style={{
            height: `${CHART_PANEL_HEIGHT}%`,
            width: "100%",
            position: "relative",
            marginBottom: isOpen ? 0 : -height,
            transition: `margin ${PANEL_TOGGLE_ANIMATION_DURATION}s`,
          }}
        >
          <BottomToggle
            isOpen={isOpen}
            handleToggle={handleToggle}
            zIndex={100}
          />
          <LoadingOverlay
            zIndex={100}
            loaderProps={{ size: 30 }}
            visible={isRouting || areElevationsLoading}
            overlayProps={{
              blur: 2,
            }}
          />
          <Paper
            radius={0}
            style={{
              zIndex: 50,
              height: "100%",
              width: "100%",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            shadow="md"
          >
            <ChartPanelMenu
              routing={routing}
              xAxisType={xAxisType}
              yAxisType={yAxisType}
              setXAxisType={setXAxisType}
              setYAxisType={setYAxisType}
              getYAxis={getYAxis}
              getTooltipYAxisIcon={getTooltipYAxisIcon}
              disabledXAxisToggle={!stats?.totalTime}
            />
            <ChartMarkerClearButton />
            {currentDataset && mainChartData && mainChartData.length > 0 && (
              <Box style={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={null}>
                  <TrackChart
                    unitType={units}
                    dataset={currentDataset}
                    chartRef={chartRef}
                    chartData={mainChartData}
                    xAxisLabelFormatter={xAxisLabelFormatter}
                    yAxisLabelFormatter={yAxisLabelFormatter}
                    yAxisTooltipFormatter={yAxisTooltipFormatter}
                    xAxisTooltipFormatter={xAxisTooltipFormatter}
                    chartTooltipProps={{
                      xAxisIcon: getTooltipXAxisIcon(),
                      yAxisIcon: getTooltipYAxisIcon(),
                    }}
                  />
                </Suspense>
                <ChartHoverSync
                  chartRef={chartRef}
                  points={currentDataset.data}
                  formatters={hoverFormatters}
                />
              </Box>
            )}
          </Paper>
        </Box>
      ) : null,
    [
      currentDataset,
      mainChartData,
      ref,
      isOpen,
      height,
      routing,
      isRouting,
      areElevationsLoading,
      xAxisType,
      yAxisType,
      units,
      xAxisLabelFormatter,
      yAxisLabelFormatter,
      yAxisTooltipFormatter,
      xAxisTooltipFormatter,
      hoverFormatters,
      getTooltipXAxisIcon,
      getTooltipYAxisIcon,
      getYAxis,
      stats?.totalTime,
    ]
  );
};
